import { useRef, useState } from 'react';
import { getMissingCalcertError } from '../utils/calcert-error';
import { MissingCalCertController } from '../components/CalCertWarningModal/CalCertWarningModal';

type UpdateController = (params: { id: string; formData: Record<string, unknown> }) => Promise<unknown>;
type CalCertGuardedAction = (options?: { skipCalCertCheck?: boolean }) => Promise<void>;

// Wraps an action (starting a cycle, generating a report) that the backend
// may reject with a CONTROLLERS_MISSING_VALID_CALCERT error. On that specific
// error, surfaces the offending controllers instead of the action's normal
// error handling; any other error is re-thrown untouched. The action is
// passed to run() itself (not bound up front) so one hook instance can guard
// several dynamically-chosen actions, e.g. one Play button per row in a
// table, and forwards a skipCalCertCheck flag through to the action so it
// can be re-attempted either permanently fixed (disableCheckAndRetry, which
// flips the controller's calcerts_not_needed flag) or bypassed just this
// once (proceedWithoutCalCert, which never touches that flag).
export const useCalCertGuardedAction = (updateController: UpdateController) => {
  const [missingControllers, setMissingControllers] = useState<MissingCalCertController[] | null>(null);
  const lastActionRef = useRef<CalCertGuardedAction | null>(null);

  const run = async (action: CalCertGuardedAction) => {
    lastActionRef.current = action;

    try {
      await action();
      setMissingControllers(null);
    } catch (error) {
      const calcertError = getMissingCalcertError(error);

      if (calcertError) {
        setMissingControllers(calcertError.controllers);
        return;
      }

      throw error;
    }
  };

  const closeModal = () => setMissingControllers(null);

  const disableCheckAndRetry = async (controllerId: string) => {
    await updateController({ id: controllerId, formData: { calcerts_not_needed: true } });

    if (lastActionRef.current) {
      await run(lastActionRef.current);
    }
  };

  const proceedWithoutCalCert = async () => {
    if (!lastActionRef.current) {
      return;
    }

    await lastActionRef.current({ skipCalCertCheck: true });
    setMissingControllers(null);
  };

  return { run, missingControllers, closeModal, disableCheckAndRetry, proceedWithoutCalCert };
};
